import { createBatchLoader, type BatchLoader } from "@/queries/dataloader";
import type { PlainOrganizationMember } from "@/queries/organization-queries";
import { toPlainMessage } from "@bufbuild/protobuf";
import type { GitpodAPI } from "@/api";

export type MemberLoader = BatchLoader<PlainOrganizationMember>;

const createMemberLoader = (api: GitpodAPI, organizationId: string): MemberLoader =>
    createBatchLoader(async (userIds) => {
        const results = new Map<string, PlainOrganizationMember>();
        const response = await api.organizationService.listMembers({
            organizationId,
            filter: { userIds },
        });
        for (const m of response.members) {
            const plain = toPlainMessage(m);
            results.set(plain.userId, plain);
        }

        return results;
    });

/**
 * Module-level singleton loader, shared across all components.
 *
 * Every component that calls getMemberLoader() gets the same instance
 * (per org), enabling cross-component batching.
 */
let sharedLoader: { loader: MemberLoader; orgId: string; api: GitpodAPI } | null = null;

export const getMemberLoader = (api: GitpodAPI, organizationId: string): MemberLoader => {
    if (!sharedLoader || sharedLoader.orgId !== organizationId || sharedLoader.api !== api) {
        sharedLoader = {
            loader: createMemberLoader(api, organizationId),
            orgId: organizationId,
            api,
        };
    }

    return sharedLoader.loader;
};
